import { useCallback, useEffect, useRef, useState } from 'react';
import {
    validateMarkdownFile,
    validateMarkdownFiles,
    type MarkdownValidationOptions,
    type MarkdownValidationResult,
} from './markdownFileValidator';

export type ValidationStatus = 'idle' | 'validating' | 'valid' | 'invalid';

export interface UseMarkdownFileValidatorResult {
    validate: (file: File | Blob) => Promise<MarkdownValidationResult | null>;
    validateMany: (files: Iterable<File | Blob> | ArrayLike<File | Blob>) => Promise<MarkdownValidationResult[] | null>;
    reset: () => void;
    status: ValidationStatus;
    isValidating: boolean;
    result: MarkdownValidationResult | null;
    results: MarkdownValidationResult[];
    error: string | null;
}

export function useMarkdownFileValidator(options: MarkdownValidationOptions = {}): UseMarkdownFileValidatorResult {
    const [status, setStatus] = useState<ValidationStatus>('idle');
    const [results, setResults] = useState<MarkdownValidationResult[]>([]);
    const [result, setResult] = useState<MarkdownValidationResult | null>(null);

    const optionsRef = useRef(options);
    optionsRef.current = options;

    const controllerRef = useRef<AbortController | null>(null);
    const runIdRef = useRef(0);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            controllerRef.current?.abort();
        };
    }, []);

    const beginRun = useCallback((): { id: number; opts: MarkdownValidationOptions } => {
        controllerRef.current?.abort();
        const controller = new AbortController();
        controllerRef.current = controller;
        const id = ++runIdRef.current;

        const external = optionsRef.current.signal;
        if (external) {
            if (external.aborted) controller.abort();
            else external.addEventListener('abort', () => controller.abort(), { once: true });
        }

        setStatus('validating');
        return { id, opts: { ...optionsRef.current, signal: controller.signal } };
    }, []);

    const isCurrent = useCallback((id: number): boolean => mountedRef.current && runIdRef.current === id, []);

    const validate = useCallback(
        async (file: File | Blob): Promise<MarkdownValidationResult | null> => {
            const { id, opts } = beginRun();
            const value = await validateMarkdownFile(file, opts);
            if (!isCurrent(id)) return null;
            setResult(value);
            setResults([value]);
            setStatus(value.valid ? 'valid' : 'invalid');
            return value;
        },
        [beginRun, isCurrent],
    );

    const validateMany = useCallback(
        async (files: Iterable<File | Blob> | ArrayLike<File | Blob>): Promise<MarkdownValidationResult[] | null> => {
            const { id, opts } = beginRun();
            const values = await validateMarkdownFiles(files, opts);
            if (!isCurrent(id)) return null;
            const representative = values.find((value) => !value.valid) ?? values[0] ?? null;
            setResults(values);
            setResult(representative);
            setStatus(values.length > 0 && values.every((value) => value.valid) ? 'valid' : 'invalid');
            return values;
        },
        [beginRun, isCurrent],
    );

    const reset = useCallback(() => {
        controllerRef.current?.abort();
        controllerRef.current = null;
        runIdRef.current++;
        setStatus('idle');
        setResult(null);
        setResults([]);
    }, []);

    return {
        validate,
        validateMany,
        reset,
        status,
        isValidating: status === 'validating',
        result,
        results,
        error: result && !result.valid ? result.errors[0].message : null,
    };
}
